import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  EXTRA_SNACKS,
  addSnack,
  canMoveMeal,
  isExtraSnack,
  isWorkoutMeal,
  moveMeal,
  orderedMeals,
  removeSnack,
  setMealReminder,
  setMealTime,
} from '@kraftwerk/shared';
import type { MealSetting } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import { readProfile, updateMeals } from '../../db/profile.js';
import { useIsChild } from '../../hooks/useModules.js';

/**
 * Mahlzeiten anpassen (16.10.2026): Reihenfolge, Uhrzeit, Erinnerung und bis
 * zu drei weitere Snacks. Seit 18.10.2026: Mit Uhrzeit sortiert die Uhrzeit,
 * verschieben lässt sich nur, was keine hat; Pre/Post liegen immer ums
 * Training und haben weder Uhrzeit noch Platz. Derselbe Baustein im Tag und in den Einstellungen —
 * ein zweiter Eingang, kein zweiter Weg. Jede Änderung wird sofort gespeichert.
 */
export function MealsSetting({ framed = true }: { framed?: boolean }): React.JSX.Element | null {
  const { t } = useTranslation();
  // Kinder haben keine Supplements — der Hinweis darauf verwirrt nur.
  const isChild = useIsChild();
  const meals = useLiveQuery(async () => (await readProfile(db)).meals, [], undefined);
  if (!meals) return null;

  const save = (next: MealSetting[] | null) => {
    if (next) void updateMeals(next, db);
  };
  const extras = meals.filter((meal) => isExtraSnack(meal.slot)).length;
  const ordered = orderedMeals(meals);
  const own = ordered.filter((meal) => !isWorkoutMeal(meal.slot));

  return (
    <section
      aria-labelledby="settings-meals"
      className={framed ? 'kw-tile flex flex-col gap-3' : 'flex flex-col gap-3'}
    >
      <h4 id="settings-meals" className={framed ? 'kw-kicker' : 'sr-only'}>
        {t('meals.heading')}
      </h4>
      <p className="text-sm text-ink-muted">{isChild ? t('meals.introChild') : t('meals.intro')}</p>
      <ol className="flex flex-col gap-2">
        {own.map((meal, index) => (
          <MealRow
            key={meal.slot}
            meal={meal}
            first={index === 0}
            last={index === own.length - 1}
            onChange={(next) => save(next(meals))}
          />
        ))}
      </ol>
      <div className="flex flex-col gap-1 rounded-xl border border-line px-3 py-2">
        <p className="font-medium">
          {t('nutrition.slot.pre')} · {t('nutrition.slot.post')}
        </p>
        <p className="text-xs text-ink-muted">{t('meals.workoutNote')}</p>
      </div>
      {extras < EXTRA_SNACKS.length ? (
        <button
          type="button"
          onClick={() => save(addSnack(meals))}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('meals.addSnack', { count: EXTRA_SNACKS.length - extras })}
        </button>
      ) : (
        <p className="text-xs text-ink-muted">{t('meals.snacksFull')}</p>
      )}
    </section>
  );
}

function MealRow({
  meal,
  first,
  last,
  onChange,
}: {
  meal: MealSetting;
  first: boolean;
  last: boolean;
  onChange: (change: (list: MealSetting[]) => MealSetting[]) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const timeId = useId();
  const name = t(`nutrition.slot.${meal.slot}`);

  return (
    <li className="flex flex-col gap-2 rounded-xl border border-line px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 truncate font-medium">{name}</span>
        <span className="flex shrink-0 gap-1">
          {canMoveMeal(meal) ? (
            <>
              <button
                type="button"
                disabled={first}
                onClick={() => onChange((list) => moveMeal(list, meal.slot, -1))}
                aria-label={t('meals.up', { name })}
                className="kw-btn kw-btn-quiet kw-btn-small px-3"
              >
                ↑
              </button>
              <button
                type="button"
                disabled={last}
                onClick={() => onChange((list) => moveMeal(list, meal.slot, 1))}
                aria-label={t('meals.down', { name })}
                className="kw-btn kw-btn-quiet kw-btn-small px-3"
              >
                ↓
              </button>
            </>
          ) : null}
          {isExtraSnack(meal.slot) ? (
            <button
              type="button"
              onClick={() => onChange((list) => removeSnack(list, meal.slot))}
              aria-label={t('meals.remove', { name })}
              className="kw-btn kw-btn-quiet kw-btn-small px-3 hover:border-danger hover:text-danger"
            >
              ×
            </button>
          ) : null}
        </span>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label htmlFor={timeId} className="kw-label flex flex-col gap-1.5">
          {t('meals.time')}
          <input
            id={timeId}
            type="time"
            aria-label={t('meals.timeNamed', { name })}
            value={meal.time ?? ''}
            onChange={(event) =>
              onChange((list) => setMealTime(list, meal.slot, event.target.value || null))
            }
            className="kw-field w-32"
          />
        </label>
        <button
          type="button"
          aria-pressed={meal.reminder}
          disabled={meal.time === null}
          onClick={() => onChange((list) => setMealReminder(list, meal.slot, !meal.reminder))}
          aria-label={t('meals.reminderNamed', { name })}
          className="kw-toggle disabled:opacity-40"
        >
          {meal.reminder ? t('meals.reminderOn') : t('meals.reminderOff')}
        </button>
      </div>
      <p className="text-xs text-ink-muted">
        {meal.time === null ? t('meals.noTime') : t('meals.byTime')}
      </p>
    </li>
  );
}
